import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ClipboardList,
  Pill,
  History,
  CheckCircle2,
  Loader2,
  Activity,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PatientBanner } from "@/components/medico/PatientBanner";
import { AnamneseTab } from "@/components/medico/AnamneseTab";
import { MemedEmbed } from "@/components/medico/MemedEmbed";
import { HistoricoTab } from "@/components/medico/HistoricoTab";
import { ACTIVE_PATIENTS } from "@/data/medicoData";

export default function Atendimento() {
  const [patientId, setPatientId] = useState(ACTIVE_PATIENTS[0].id);
  const [anamnese, setAnamnese] = useState("");
  const [concluded, setConcluded] = useState(false);
  const [sending, setSending] = useState(false);

  const patient = ACTIVE_PATIENTS.find((p) => p.id === patientId);

  const switchPatient = (id) => {
    setPatientId(id);
    setAnamnese("");
    setConcluded(false);
  };

  const concludeAtendimento = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setConcluded(true);
      toast.success("Atendimento concluído!", {
        description: `Resumo enviado ao WhatsApp de ${patient.name} · Status atualizado para "Atendido" na agenda.`,
      });
    }, 900);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 shadow-[0_4px_12px_rgba(37,99,235,0.35)]">
              <Activity className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">
                VeloClin
              </span>
              <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                Prontuário
              </span>
            </div>
          </div>
          <Link
            to="/agenda"
            data-testid="back-to-agenda"
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar à Agenda
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] space-y-5 px-5 py-6 md:px-8 md:py-8">
        <PatientBanner
          patient={patient}
          patients={ACTIVE_PATIENTS}
          onSwitch={switchPatient}
        />

        <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] md:p-6">
          <Tabs defaultValue="anamnese" className="w-full">
            <TabsList className="mb-5 grid w-full grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:w-auto sm:inline-grid">
              <TabsTrigger
                value="anamnese"
                data-testid="tab-anamnese"
                className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
              >
                <ClipboardList className="h-4 w-4" />
                <span className="hidden sm:inline">Anamnese</span>
                <span className="sm:hidden">Evol.</span>
              </TabsTrigger>
              <TabsTrigger
                value="receituario"
                data-testid="tab-receituario"
                className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
              >
                <Pill className="h-4 w-4" />
                <span className="hidden sm:inline">Prescrição &amp; Exames</span>
                <span className="sm:hidden">Receituário</span>
              </TabsTrigger>
              <TabsTrigger
                value="historico"
                data-testid="tab-historico"
                className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
              >
                <History className="h-4 w-4" />
                Histórico
              </TabsTrigger>
            </TabsList>

            <TabsContent value="anamnese" className="mt-0 focus-visible:outline-none">
              <AnamneseTab value={anamnese} onChange={setAnamnese} />
            </TabsContent>
            <TabsContent value="receituario" className="mt-0 focus-visible:outline-none">
              <MemedEmbed />
            </TabsContent>
            <TabsContent value="historico" className="mt-0 focus-visible:outline-none">
              <HistoricoTab history={patient.history} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Sticky footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200/70 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between gap-4 px-5 py-3 md:px-8">
          <div className="flex items-center gap-2 text-sm">
            {concluded ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span className="font-medium text-emerald-600" data-testid="atendimento-status">
                  Atendimento concluído
                </span>
              </>
            ) : (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
                </span>
                <span className="font-medium text-slate-500" data-testid="atendimento-status">
                  Atendimento em andamento...
                </span>
              </>
            )}
          </div>

          <motion.button
            whileTap={{ scale: 0.98 }}
            data-testid="conclude-atendimento-btn"
            onClick={concludeAtendimento}
            disabled={concluded || sending}
            className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-[0_4px_14px_rgba(5,150,105,0.4)] transition-colors duration-200 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando resumo...
              </>
            ) : concluded ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Resumo enviado
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Concluir Atendimento &amp; Enviar Resumo
              </>
            )}
          </motion.button>
        </div>
      </footer>
    </div>
  );
}
